import { useState } from 'react'
import { CheckCircle2, XCircle, Play, ChevronDown, ChevronRight } from 'lucide-react'
import './RokuSettings.css'

const FEED_URL = 'https://watch.tenband.tv/api/roku-search-feed'
const VOD_URL = 'https://watch.tenband.tv/api/vod'
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/

// Measure image natural dimensions in the browser, batched.
function measureImages(urls, onProgress) {
  const results = new Map()
  let done = 0
  const queue = [...urls]
  const worker = async () => {
    while (queue.length) {
      const url = queue.shift()
      const dims = await new Promise(res => {
        const img = new Image()
        img.onload = () => res({ w: img.naturalWidth, h: img.naturalHeight })
        img.onerror = () => res(null)
        img.src = url
      })
      results.set(url, dims)
      done++
      onProgress(done, urls.length)
    }
  }
  return Promise.all(Array.from({ length: 10 }, worker)).then(() => results)
}

export default function RokuFeedValidator() {
  const [running, setRunning] = useState(false)
  const [checks, setChecks] = useState(null)
  const [meta, setMeta] = useState(null)
  const [imgProgress, setImgProgress] = useState(null)
  const [error, setError] = useState('')
  const [open, setOpen] = useState({})

  async function run() {
    setRunning(true); setError(''); setChecks(null); setMeta(null); setImgProgress(null)
    try {
      const bust = `?v=${Date.now()}`
      const [feed, vod] = await Promise.all([
        fetch(FEED_URL + bust).then(r => r.json()),
        fetch(VOD_URL + bust).then(r => r.json()).catch(() => null),
      ])
      const assets = feed.assets || []
      const TIMED = new Set(['shortForm', 'tvSpecial', 'movie', 'episode']) // playable VOD types (need duration)
      const timed = assets.filter(a => TIMED.has(a.type))
      const shortForm = assets.filter(a => a.type === 'shortForm')
      const tvSpecial = assets.filter(a => a.type === 'tvSpecial')
      const liveAssets = assets.filter(a => a.type === 'liveStream')

      const vodIds = new Set()
      if (vod && vod.categories) {
        for (const k of Object.keys(vod.categories)) for (const it of (vod.categories[k] || [])) if (it?.id) vodIds.add(it.id)
      }

      const label = a => a.titles?.[0]?.value || a.id
      const imgUrl = a => a.images?.[0]?.url || ''
      const results = []
      const add = (key, lbl, failItems, note) =>
        results.push({ key, label: lbl, pass: failItems.length === 0, failCount: failItems.length, items: failItems.slice(0, 100), note })

      const seenIds = new Set(); const dupIds = []
      for (const a of assets) { if (seenIds.has(a.id)) dupIds.push(a); else seenIds.add(a.id) }

      // Pure-JSON checks (Roku Search Feed spec)
      add('yt', 'No YouTube-hosted images', assets.filter(a => imgUrl(a).includes('i.ytimg.com')).map(a => ({ id: a.id, title: label(a), detail: imgUrl(a) })))
      add('dur', 'Timed assets have durationInSeconds', timed.filter(a => !(a.durationInSeconds > 0)).map(a => ({ id: a.id, title: label(a), detail: `${a.type} missing durationInSeconds` })))
      add('shortdur', 'shortForm ≤ 15 min (900s)', shortForm.filter(a => (a.durationInSeconds || 0) > 900).map(a => ({ id: a.id, title: label(a), detail: `${a.durationInSeconds}s — should be tvSpecial` })))
      add('desc', 'Long description ≠ short description', assets.filter(a => a.longDescriptions?.[0]?.value && a.longDescriptions[0].value === a.shortDescriptions?.[0]?.value).map(a => ({ id: a.id, title: label(a), detail: 'long == short' })))
      add('date', 'releaseDate is ISO YYYY-MM-DD (when present)', assets.filter(a => a.releaseDate && !DATE_RE.test(a.releaseDate)).map(a => ({ id: a.id, title: label(a), detail: a.releaseDate })))
      add('reqdate', 'Has releaseDate or releaseYear', assets.filter(a => !a.releaseDate && !a.releaseYear).map(a => ({ id: a.id, title: label(a), detail: 'missing releaseDate/releaseYear' })))
      add('rating', 'advisoryRatings present', assets.filter(a => !(a.advisoryRatings?.length)).map(a => ({ id: a.id, title: label(a), detail: 'missing advisoryRatings' })))
      add('req', 'Required fields present', assets.filter(a => !a.id || !a.titles?.[0]?.value || !(a.shortDescriptions?.length) || !(a.genres?.length) || !(a.images?.length) || !a.content?.playOptions?.[0]?.playId).map(a => ({ id: a.id, title: label(a), detail: 'missing id/title/shortDesc/genres/images/playId' })))
      add('idlen', 'id ≤ 50 chars & unique', [
        ...assets.filter(a => (a.id || '').length > 50).map(a => ({ id: a.id, title: label(a), detail: `${(a.id || '').length} chars (max 50)` })),
        ...dupIds.map(a => ({ id: a.id, title: label(a), detail: 'duplicate id' })),
      ])
      add('resolve', 'VOD id resolves in /api/vod (deep-link)', vod ? timed.filter(a => !vodIds.has(a.id)).map(a => ({ id: a.id, title: label(a), detail: 'not in /api/vod (deep-link would fail)' })) : [], vod ? null : '/api/vod unavailable — skipped')
      add('live', 'No liveStream assets (deferred)', liveAssets.map(a => ({ id: a.id, title: label(a), detail: 'liveStream — needs Roku mediaType=live change' })), liveAssets.length ? 'live included via settings' : null)

      // Image aspect (measured)
      const urls = [...new Set(assets.map(imgUrl).filter(Boolean))]
      setChecks(results)
      setMeta({ total: assets.length, shortForm: shortForm.length, tvSpecial: tvSpecial.length, live: liveAssets.length, lastUpdated: feed.lastUpdated })
      setImgProgress({ done: 0, total: urls.length })
      const dims = await measureImages(urls, (done, total) => setImgProgress({ done, total }))
      const imgFails = []
      for (const a of assets) {
        const u = imgUrl(a); if (!u) continue
        const d = dims.get(u)
        if (!d) imgFails.push({ id: a.id, title: label(a), detail: 'image unreachable / failed to load' })
        else {
          const ar = d.w / d.h
          const ok = (ar >= 1.72 && ar <= 1.85) || (ar >= 0.62 && ar <= 0.71) // 16:9 or 2:3
          if (!ok) imgFails.push({ id: a.id, title: label(a), detail: `${d.w}×${d.h} (aspect ${ar.toFixed(2)}, need 16:9 or 2:3)` })
          else if (d.w > 1920 || d.h > 1080) imgFails.push({ id: a.id, title: label(a), detail: `${d.w}×${d.h} exceeds max 1920×1080` })
        }
      }
      add('aspect', 'Images are 16:9 or 2:3, ≤1920×1080 & reachable', imgFails)
      setChecks([...results])
      setImgProgress(null)
    } catch (e) {
      setError(e.message || 'Validation failed')
    } finally {
      setRunning(false)
    }
  }

  const allPass = checks && checks.every(c => c.pass)

  return (
    <div className="cmpl-panel">
      <div className="cmpl-subhead">
        <p>
          Runs Roku Search Feed compliance checks against the live <code>/api/roku-search-feed</code>
          (mirrors Roku's external Feed Validator). Image aspect is measured by loading each image.
        </p>
        <div className="cmpl-toolbar">
          <button className="cmpl-btn cmpl-btn-primary" onClick={run} disabled={running}>
            <Play size={14} /> {running ? 'Validating…' : 'Run validation'}
          </button>
          {meta && (
            <span className="cmpl-count">
              {meta.total} assets ({meta.shortForm} shortForm + {meta.tvSpecial} tvSpecial{meta.live ? ` + ${meta.live} live` : ''})
              {meta.lastUpdated ? ` · updated ${new Date(meta.lastUpdated).toLocaleString()}` : ''}
            </span>
          )}
        </div>
        {imgProgress && (
          <div className="cmpl-progress">Measuring images… {imgProgress.done}/{imgProgress.total}</div>
        )}
      </div>

      <div className="cmpl-body">
        {error && <div className="cmpl-msg cmpl-error"><XCircle size={16} /> {error}</div>}
        {checks && (
          <div className={`rv-summary ${allPass ? 'ok' : 'bad'}`}>
            {allPass ? <><CheckCircle2 size={18} /> All checks passed — feed is Roku-compliant.</>
              : <><XCircle size={18} /> {checks.filter(c => !c.pass).length} check(s) failing.</>}
          </div>
        )}
        {checks && (
          <div className="rv-checks">
            {checks.map(c => (
              <div key={c.key} className={`rv-check ${c.pass ? 'pass' : 'fail'}`}>
                <div className="rv-check-head" onClick={() => c.failCount && setOpen(o => ({ ...o, [c.key]: !o[c.key] }))}>
                  {c.pass ? <CheckCircle2 size={16} className="rv-ic ok" /> : <XCircle size={16} className="rv-ic bad" />}
                  <span className="rv-check-label">{c.label}</span>
                  {c.note && <span className="rv-note">{c.note}</span>}
                  <span className="rv-spacer" />
                  {c.failCount > 0
                    ? <>{c.failCount} failing {open[c.key] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</>
                    : <span className="rv-ok-text">OK</span>}
                </div>
                {open[c.key] && c.items.length > 0 && (
                  <div className="rv-items">
                    {c.items.map((it, idx) => (
                      <div key={it.id + idx} className="rv-item">
                        <span className="rv-item-title" title={it.title}>{it.title}</span>
                        <span className="rv-item-id">{it.id}</span>
                        <span className="rv-item-detail">{it.detail}</span>
                      </div>
                    ))}
                    {c.failCount > c.items.length && <div className="rv-item rv-more">…and {c.failCount - c.items.length} more</div>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
